import { FxAlert, FxButton, FxPanel, FxSpinner } from '@/components/fx'
import type { LlmUsageSummary } from '@/types/domain'
import { COST_UNKNOWN, costPerCall, formatCount, formatUsd, purposeLabel } from './format'
import { LLM_CALL_LIMIT, useLlmCalls } from './useLlmCalls'

/**
 * "Protokoll und Kosten" — every call to a language model this form made,
 * with what it is estimated to have cost.
 *
 * The artboard puts this behind a button inside "KI-Zweitmeinung" while
 * listing it as a section in the side-nav. Both: the section carries the
 * summary, which is what someone wants at a glance, and the full per-call
 * table stays in the dialog, where a wide table belongs. A nav entry that
 * scrolled to nothing was the alternative.
 *
 * The figures are **list prices** — the gateway's table is hand-maintained
 * and knows nothing about an enterprise discount, the Batch API's 50 % or a
 * partner rate. And a call whose model is not in that table comes back
 * `priced: false` with zero amounts: that is *unknown*, never free, and is
 * counted out separately rather than folded into the total as a zero.
 */
export function LogSection({ templateId, onOpenLog }: { templateId: string; onOpenLog: () => void }) {
  const calls = useLlmCalls(templateId)
  const summary = calls.data?.summary

  return (
    <FxPanel
      title="Protokoll und Kosten"
      action={
        <FxButton variant="tonal" onClick={onOpenLog} disabled={(calls.data?.calls.length ?? 0) === 0}>
          Alle Aufrufe
        </FxButton>
      }
      className="p-5 xs:p-8 sm:p-10"
    >
      {calls.isLoading ? (
        <FxSpinner label="Protokoll wird geladen …" />
      ) : calls.forbidden ? (
        <p className="m-0 text-body text-text-muted">
          Für das Protokoll dieses Musterformulars fehlen Ihnen die Rechte.
        </p>
      ) : calls.isError ? (
        <FxAlert tone="danger" title="Das Protokoll konnte nicht geladen werden">
          <FxButton variant="outlined" className="mt-4" onClick={() => void calls.refetch()}>
            Erneut versuchen
          </FxButton>
        </FxAlert>
      ) : (calls.data?.calls.length ?? 0) === 0 ? (
        <p className="m-0 text-body text-text-muted">
          Noch keine Aufrufe. Sobald eine KI-Prüfung läuft, steht hier jeder Aufruf mit seinen
          Kosten — auch ein fehlgeschlagener.
        </p>
      ) : (
        summary && <CostSummary summary={summary} />
      )}
    </FxPanel>
  )
}

/**
 * The headline figure and one row per kind of operation.
 *
 * The per-call average is the number people actually ask about: "was würden
 * 2000 Dokumente kosten?" is that average times 2000, and no other figure on
 * this screen answers it.
 */
export function CostSummary({ summary }: { summary: LlmUsageSummary }) {
  const total = summary.total
  const tokens =
    total.inputTokens + total.outputTokens + total.cacheWriteTokens + total.cacheReadTokens

  return (
    <div className="rounded-sm bg-ground p-4">
      <p className="m-0 flex flex-wrap items-baseline gap-2">
        <span className="text-tile-title text-text">{formatUsd(total.cost.totalUsd)}</span>
        <span className="text-small text-text-muted">
          {summary.truncated
            ? `geschätzt, die letzten ${formatCount(total.calls)} Aufrufe (mehr als ${LLM_CALL_LIMIT} vorhanden)`
            : `geschätzt, ${formatCount(total.calls)} Aufrufe`}
        </span>
      </p>
      <p className="m-0 mt-1 text-small text-text-muted">
        {formatCount(tokens)} Token · Listenpreise, ohne Rabatte
      </p>
      {total.unpricedCalls > 0 && (
        <p className="m-0 mt-1 text-small text-warning-text">
          {formatCount(total.unpricedCalls)} Aufruf(e) mit unbekanntem Modell sind nicht
          eingerechnet — ihre Kosten sind {COST_UNKNOWN}, nicht null.
        </p>
      )}

      {summary.groups.length > 0 && (
        <table className="mt-4 w-full border-collapse text-small">
          <caption className="sr-only">Kosten je Vorgang</caption>
          <thead>
            <tr className="text-left text-text-muted">
              <th scope="col" className="py-1 font-normal">
                Vorgang
              </th>
              <th scope="col" className="py-1 font-normal">
                Aufrufe
              </th>
              <th scope="col" className="py-1 font-normal">
                je Aufruf
              </th>
              <th scope="col" className="py-1 text-right font-normal">
                gesamt
              </th>
            </tr>
          </thead>
          <tbody>
            {summary.groups.map((group) => {
              const average = costPerCall(group)
              return (
                <tr key={group.purpose ?? 'gesamt'}>
                  <td className="py-1">{purposeLabel(group.purpose ?? '')}</td>
                  <td className={group.failed > 0 ? 'py-1 text-warning-text' : 'py-1'}>
                    {formatCount(group.calls)}×
                    {group.failed > 0 && ` · ${formatCount(group.failed)} Fehler`}
                  </td>
                  <td className="py-1">
                    {group.cost.priced && average != null ? `${formatUsd(average)}` : COST_UNKNOWN}
                  </td>
                  <td className="py-1 text-right font-semibold">
                    {group.cost.priced ? formatUsd(group.cost.totalUsd) : COST_UNKNOWN}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      )}
    </div>
  )
}
